import React, { useEffect, useMemo, useRef, useState } from 'react'
import { QUOTE_SETTINGS } from '../data/quoteSettings'
import { storage } from '../firebase'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import coverImage from '../assets/Cover.png'
import constructionImage from '../assets/Construction.png'
import approxWeightImage from '../assets/ApproxWeight.png'
import manufacturingImage from '../assets/Manufacturing.png'
import pickupCoverImage from '../assets/PickupCover.png'
import deliveryCoverImage from '../assets/DeliveryCover.png'
import deliveryImage from '../assets/Delivery.png'
import pickupImage from '../assets/Pickup.png'
import tileImage from '../assets/Tile.png'
import phoneIcon from '../assets/Phone.png'
import emailIcon from '../assets/Email.png'
import mapIcon from '../assets/Map.png'
import Upload from '../assets/Upload.png'

const currency = value => `${QUOTE_SETTINGS.currencySymbol}${value.toFixed(2)}`

const getBasePrice = (width, depth) => {
  const match = QUOTE_SETTINGS.basePriceMatrix.find(
    row =>
      width >= row.widthMin &&
      width <= row.widthMax &&
      depth >= row.depthMin &&
      depth <= row.depthMax
  )
  return match ? match.price : 0
}

const getPieceLabel = (width, depth) => (Number(width) > 1200 || Number(depth) > 1200 ? '2 Piece' : '1 Piece')

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const estimateWeightKg = ({ width, depth, height }) => {
  const widthM = width / 1000
  const depthM = depth / 1000
  return widthM * depthM * 13.5 + 1
}

const ShowerImageCard = () => (
  <div className="quote-preview-card">
    {/* <div className="quote-preview-header">Shower base preview</div> */}
    <div className="quote-preview-image">
      <img src={coverImage} alt="Custom shower base cover" />
    </div>
  </div>
)

const StepHeader = ({ title, subtitle, bold }) => (
  <div className="quote-step-header">
    <h2>{title}</h2>
    <p style={bold ? { fontSize: '24px', fontWeight: 'bold' } : {}}>{subtitle}</p>
  </div>
)

const QUOTE_STEP_LABELS = ['Specifications', 'Quick Quote', 'Contact Details', 'Review & Submit', 'Your Estimate']

const StepTabs = ({ step }) => {
  const steps = QUOTE_STEP_LABELS
  const activeConnectorIndex = Math.min(step, steps.length - 1)
  const isLastStep = step === steps.length
  return (
    <div
      className={`quote-steps${isLastStep ? ' is-last-step' : ''}`}
      style={{
        '--step-gap': '20px',
        '--step-count': steps.length,
        '--active-connector-index': activeConnectorIndex,
      }}
    >
      {steps.map((label, index) => {
        const current = index + 1 === step
        const complete = index + 1 <= step
        return (
          <div
            key={label}
            className={`quote-step-item${current ? ' active' : ''}${complete ? ' done' : ''}`}
          >
            <div className="step-dot" aria-hidden="true" />
            <div>
              <span>{label}</span>
              <small>Step {index + 1}</small>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const StepIndicator = ({ step, total }) => {
  const percent = total > 1 ? ((step - 1) / (total - 1)) * 100 : 0
  const currentStep = Math.min(Math.max(step, 1), total)
  const currentLabel = QUOTE_STEP_LABELS[currentStep - 1] || ''
  return (
    <div className="mobile-step-indicator">
      <div className="step-indicator-label">Step {currentStep}: {currentLabel}</div>
      <div className="step-indicator-track">
        <span style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

const TooltipIcon = ({ text, align = 'default' }) => {
  const [mobileTop, setMobileTop] = useState(null)
  const [mobileTransform, setMobileTransform] = useState('translate(-50%, -100%)')
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const tooltipRef = useRef(null)

  const positionMobileTooltip = event => {
    if (typeof window === 'undefined' || window.innerWidth > 640) return
    const rect = event.currentTarget.getBoundingClientRect()
    const estimatedTooltipHeight = 150
    const gap = 8
    const hasSpaceAbove = rect.top >= estimatedTooltipHeight + gap

    if (hasSpaceAbove) {
      setMobileTop(rect.top - gap)
      setMobileTransform('translate(-50%, -100%)')
      return
    }

    setMobileTop(rect.bottom + gap)
    setMobileTransform('translateX(-50%)')
  }

  const suppressFieldFocus = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleTooltipActivate = event => {
    suppressFieldFocus(event)
    positionMobileTooltip(event)
    setIsTooltipOpen(prev => !prev)
  }

  useEffect(() => {
    if (!isTooltipOpen) return

    const closeOnOutsideTap = event => {
      if (!tooltipRef.current || tooltipRef.current.contains(event.target)) return
      setIsTooltipOpen(false)
    }

    document.addEventListener('touchstart', closeOnOutsideTap)
    document.addEventListener('mousedown', closeOnOutsideTap)
    return () => {
      document.removeEventListener('touchstart', closeOnOutsideTap)
      document.removeEventListener('mousedown', closeOnOutsideTap)
    }
  }, [isTooltipOpen])

  return (
    <span
      ref={tooltipRef}
      className={`tooltip-wrap${align === 'end' ? ' tooltip-end' : ''}${isTooltipOpen ? ' is-open' : ''}`}
      tabIndex={0}
      aria-label={text}
      onMouseEnter={positionMobileTooltip}
      onFocus={event => {
        positionMobileTooltip(event)
        setIsTooltipOpen(true)
      }}
      onBlur={() => setIsTooltipOpen(false)}
      onPointerDown={suppressFieldFocus}
      onClick={handleTooltipActivate}
      onTouchStart={handleTooltipActivate}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleTooltipActivate(event)
        }
      }}
    >
      <span className="info-dot" aria-hidden="true">?</span>
      <span
        className="tooltip-bubble"
        style={mobileTop == null
          ? undefined
          : {
            '--tooltip-mobile-top': `${mobileTop}px`,
            '--tooltip-mobile-transform': mobileTransform,
          }}
      >
        {text}
      </span>
    </span>
  )
}

const ChevronIcon = ({ direction = 'right' }) => (
  <span className={`btn-arrow${direction === 'left' ? ' left' : ''}`} aria-hidden="true">
    <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M0.244078 0.244078C0.569515 -0.0813592 1.09715 -0.0813592 1.42259 0.244078L7.25592 6.07741C7.58136 6.40285 7.58136 6.93049 7.25592 7.25592L1.42259 13.0893C1.09715 13.4147 0.569515 13.4147 0.244078 13.0893C-0.0813592 12.7638 -0.0813592 12.2362 0.244078 11.9107L5.48816 6.66667L0.244078 1.42259C-0.0813592 1.09715 -0.0813592 0.569515 0.244078 0.244078Z" fill="currentColor" />
    </svg>
  </span>
)

const InputField = ({ label, required, error, children, hint }) => (
  <div className={`quote-input ${error ? 'error' : ''}`}>
    <label className="quote-input-label">
      <span className="label-with-icon">
        {label}{required && <em>*</em>}
      </span>
      {children}
    </label>
    {hint && <small>{hint}</small>}
    {error && <div className="error-text">{error}</div>}
  </div>
)

const HelpCard = ({ onHelp }) => (
  <button type="button" className="help-card" onClick={onHelp}>
    <div className="help-card-thumb">
      <div className="play-icon">▶</div>
    </div>
    <div>
      <h4>Not sure about your shower dimensions?</h4>
      <p>Get a better understanding of how to measure your shower base.</p>
    </div>
    <span className="help-card-plus" aria-hidden="true">+</span>
  </button>
)

const HelpModal = ({ onClose }) => (
  <div className="modal-backdrop help-backdrop" role="dialog" aria-modal="true">
    <div className="modal help-modal">
      <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g opacity="0.7">
            <rect width="40" height="40" rx="20" fill="#E5E7EB" />
            <path d="M25 15L15 25M15 15L25 25" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
          </g>
        </svg>
      </button>
      <h3>How to Measure Your Shower Base</h3>
      <p>Follow these steps to accurately measure your shower base</p>
      <div className="help-video">
        <div className="help-video-preview">
          <iframe
            src={HELP_VIDEO_EMBED_URL}
            title="How to measure your shower base"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>
      <h4 className="help-tips-title">Measurement Tips:</h4>
      <ul className="help-steps">
        <li>Measure the internal width and depth of your shower space</li>
        <li>Width is the measurement from left to right (parallel to the wall with the screen)</li>
        <li>Depth is the measurement from front to back (perpendicular to the screen wall)</li>
        <li>Height is the measurement from the shower floor to the top of the entry step</li>
        <li>Double-check all measurements before submitting your quote</li>
      </ul>
    </div>
  </div>
)

const SupportModal = ({ onClose }) => (
  <div className="modal-backdrop support-backdrop" role="dialog" aria-modal="true">
    <div className="modal support-modal">
      <button type="button" className="support-close" onClick={onClose} aria-label="Close">
        –
      </button>
      <div className="support-block">
        <h3>Questions about your quote?</h3>
        <p>Contact our friendly team to request a quote, product sample, or for any other details.</p>
      </div>
      <div className="support-block support-contact">
        <a href="tel:0390872199" className="support-contact-item">
          <img src={phoneIcon} alt="" aria-hidden="true" />
          <span>(03) 9087 2199</span>
        </a>
        <a href="mailto:sales@equipsy.com.au" className="support-contact-item">
          <img src={emailIcon} alt="" aria-hidden="true" />
          <span>sales@equipsy.com.au</span>
        </a>
        <div className="support-contact-item">
          <img src={mapIcon} alt="" aria-hidden="true" />
          <span>49a Quinn St, Preston VIC 3072</span>
        </div>
      </div>
      <a className="support-cta" href="https://equipsy.com.au/contact-us/" target="_blank" rel="noreferrer">
        CONTACT US
      </a>
    </div>
  </div>
)

const FEEDBACK_SUBMIT_URL = 'https://australia-southeast1-equipsy-au.cloudfunctions.net/submitFeedback'
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const GOOGLE_PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const GOOGLE_PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places'
const HELP_VIDEO_EMBED_URL = 'https://www.youtube.com/embed/gEAcRQErUFk'

const createPlacesSessionToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const FeedbackModal = ({ onClose, onSubmitted, feedbackContext }) => {
  const [occupation, setOccupation] = useState('')
  const [source, setSource] = useState('')
  const [sourceOther, setSourceOther] = useState('')
  const [nps, setNps] = useState('')
  const [helpNeeded, setHelpNeeded] = useState('No')
  const [comments, setComments] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const canSubmit = Boolean(nps && occupation && source) && !isSubmitting

  const handleSubmit = async () => {
    if (!canSubmit) return

    setIsSubmitting(true)
    setSubmitError('')

    try {
      const response = await fetch(FEEDBACK_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nps,
          occupation,
          source,
          sourceOther: source === 'others' ? sourceOther : '',
          helpNeeded,
          comments,
          quoteId: feedbackContext?.quoteId || '',
          contactEmail: feedbackContext?.contactEmail || '',
          contactName: feedbackContext?.contactName || '',
          submittedAt: new Date().toISOString(),
          page: '/get-a-quote',
        }),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        throw new Error(errorBody?.error || 'Feedback submission failed.')
      }

      onSubmitted?.()
      onClose()
    } catch (error) {
      setSubmitError(error.message || 'Feedback submission failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop feedback-backdrop" role="dialog" aria-modal="true">
      <div className="modal modal-wide feedback-modal">
        <button type="button" className="feedback-close" onClick={onClose} aria-label="Close feedback">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity="0.7">
              <rect width="40" height="40" rx="20" fill="#E5E7EB" />
              <path d="M25 15L15 25M15 15L25 25" stroke="black" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
            </g>
          </svg>
        </button>
        <h3>How was your experience?</h3>
        <p className="feedback-subtitle">Help us improve our quote system with your feedback</p>
        <h4 className="feedback-question">How easy was it to use? (1-7)</h4>
        <div className="nps-scale">
          {Array.from({ length: 7 }, (_, idx) => idx + 1).map(value => (
            <button
              type="button"
              key={value}
              className={nps === value ? 'chip active' : 'chip'}
              onClick={() => setNps(value)}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="feedback-range-hints">
          <span>Very Difficult</span>
          <span>Very Easy</span>
        </div>
        <div className="two-col">
          <InputField label="How did you hear about us?" required>
            <select value={source} onChange={event => setSource(event.target.value)}>
              <option value="">Select</option>
              <option value="others">Others</option>
              <option value="google">Google</option>
              <option value="social">Social Media</option>
              <option value="newsletter">Newsletter</option>
              <option value="word">Word of Mouth</option>
              <option value="ebay">Ebay</option>
              <option value="events">Events</option>
            </select>
          </InputField>
          <InputField label="What is your occupation?" required>
            <select value={occupation} onChange={event => setOccupation(event.target.value)}>
              <option value="">Select</option>
              <option value="builders">Builders</option>
              <option value="ot">OT</option>
              <option value="hcp">HCP</option>
              <option value="trades">Trades</option>
              <option value="personal">Personal Project</option>
              <option value="end-user">End User</option>
              <option value="others">Others</option>
            </select>
          </InputField>
          {source === 'others' && (
            <InputField label="Please specify">
              <input value={sourceOther} onChange={event => setSourceOther(event.target.value)} />
            </InputField>
          )}
          <InputField label="Did you need to call us for help?" required>
            <div className="entry-grid">
              {['Yes', 'No'].map(option => (
                <button
                  type="button"
                  key={option}
                  className={helpNeeded === option ? 'chip active' : 'chip'}
                  onClick={() => setHelpNeeded(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </InputField>
        </div>
        <InputField label="Any additional comments? (Optional)">
          <textarea
            rows="3"
            placeholder="Tell us what worked well or what could be improved..."
            value={comments}
            onChange={event => setComments(event.target.value)}
          />
        </InputField>
        {submitError && <div className="alert">{submitError}</div>}
        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose} disabled={isSubmitting}>Skip</button>
          <button
            type="button"
            className="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  )
}

const GetQuote = () => {
  const [step, setStep] = useState(1)
  const [showHelp, setShowHelp] = useState(false)
  const [showSupport, setShowSupport] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [quoteId, setQuoteId] = useState('')
  const [pdfUrl, setPdfUrl] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [uploadError, setUploadError] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [honeypot, setHoneypot] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [addressLookupUnavailable, setAddressLookupUnavailable] = useState(false)
  const [addressPredictions, setAddressPredictions] = useState([])
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false)
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressNoResults, setAddressNoResults] = useState(false)
  const [addressActiveIndex, setAddressActiveIndex] = useState(-1)
  const deliveryAutocompleteContainerRef = useRef(null)
  const placesSessionTokenRef = useRef(createPlacesSessionToken())
  const addressAutocompleteRequestIdRef = useRef(0)
  const skipNextAutocompleteFetchRef = useRef(false)
  const previousAlertRef = useRef('')

  const [specs, setSpecs] = useState({
    width: '950',
    depth: '800',
    height: '40',
    screenCutoff: false,
  })

  const [contact, setContact] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    reference: '',
  })

  const [additionalInfo, setAdditionalInfo] = useState('')

  const [delivery, setDelivery] = useState({
    mode: 'Pickup',
    address: '',
    notes: '',
    authorityToLeave: '',
  })

  const numericSpecs = useMemo(() => ({
    width: toNumber(specs.width),
    depth: toNumber(specs.depth),
    height: toNumber(specs.height),
  }), [specs.width, specs.depth, specs.height])

  const basePrice = useMemo(
    () => getBasePrice(numericSpecs.width, numericSpecs.depth),
    [numericSpecs.width, numericSpecs.depth]
  )
  const screenCutoffFee = specs.screenCutoff ? QUOTE_SETTINGS.screenCutoffFee : 0
  const weightKg = useMemo(
    () => estimateWeightKg({ ...numericSpecs, screenCutoff: specs.screenCutoff }),
    [numericSpecs, specs.screenCutoff]
  )
  const deliveryFee = delivery.mode === 'Delivery' ? QUOTE_SETTINGS.deliveryFee : 0
  const total = basePrice + screenCutoffFee + deliveryFee

  const clearAddressErrors = () => {
    setErrors(prev => {
      if (!prev.address && !prev.deliveryValidation) return prev
      const next = { ...prev }
      delete next.address
      delete next.deliveryValidation
      return next
    })
  }

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [step, submitted])

  useEffect(() => {
    if (step !== 4 || delivery.mode !== 'Delivery') return
    if (!GOOGLE_MAPS_API_KEY) {
      setAddressLookupUnavailable(true)
      return
    }
    if (skipNextAutocompleteFetchRef.current) {
      skipNextAutocompleteFetchRef.current = false
      return
    }

    const inputValue = (delivery.address || '').trim()
    if (inputValue.length < 2) {
      setAddressPredictions([])
      setAddressDropdownOpen(false)
      setAddressLoading(false)
      setAddressNoResults(false)
      setAddressActiveIndex(-1)
      return
    }

    const currentRequestId = ++addressAutocompleteRequestIdRef.current
    const timeoutId = setTimeout(() => {
      setAddressLoading(true)
      setAddressNoResults(false)
      fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
        },
        body: JSON.stringify({
          input: inputValue,
          languageCode: 'en-AU',
          regionCode: 'AU',
          includedRegionCodes: ['AU'],
          sessionToken: placesSessionTokenRef.current,
        }),
      })
        .then(async response => {
          if (!response.ok) {
            const errorBody = await response.text().catch(() => '')
            throw new Error(errorBody || `Autocomplete request failed (${response.status})`)
          }
          return response.json()
        })
        .then(data => {
          if (currentRequestId !== addressAutocompleteRequestIdRef.current) return
          const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : []
          const items = suggestions
            .map(item => {
              const prediction = item?.placePrediction
              return prediction?.placeId
                ? {
                  place_id: prediction.placeId,
                  description: prediction?.text?.text || '',
                }
                : null
            })
            .filter(Boolean)

          setAddressLookupUnavailable(false)
          setAddressPredictions(items)
          setAddressDropdownOpen(true)
          setAddressLoading(false)
          setAddressNoResults(items.length === 0)
          setAddressActiveIndex(items.length ? 0 : -1)
        })
        .catch(() => {
          if (currentRequestId !== addressAutocompleteRequestIdRef.current) return
          setAddressLookupUnavailable(true)
          setAddressPredictions([])
          setAddressDropdownOpen(false)
          setAddressLoading(false)
          setAddressNoResults(false)
          setAddressActiveIndex(-1)
        })
    }, 220)

    return () => clearTimeout(timeoutId)
  }, [step, delivery.mode, delivery.address])

  useEffect(() => {
    if (step !== 4 || delivery.mode !== 'Delivery') return undefined

    const closeDropdown = event => {
      const root = deliveryAutocompleteContainerRef.current
      if (!root || root.contains(event.target)) return
      setAddressDropdownOpen(false)
      setAddressActiveIndex(-1)
    }

    document.addEventListener('mousedown', closeDropdown)
    document.addEventListener('touchstart', closeDropdown)
    return () => {
      document.removeEventListener('mousedown', closeDropdown)
      document.removeEventListener('touchstart', closeDropdown)
    }
  }, [step, delivery.mode])

  const selectDeliveryPrediction = prediction => {
    if (!prediction) return
    if (!GOOGLE_MAPS_API_KEY) {
      setAddressLookupUnavailable(true)
      return
    }

    fetch(
      `${GOOGLE_PLACES_DETAILS_URL}/${encodeURIComponent(prediction.place_id)}?languageCode=en-AU&regionCode=AU&sessionToken=${encodeURIComponent(placesSessionTokenRef.current)}`,
      {
        headers: {
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'formattedAddress',
        },
      }
    )
      .then(async response => {
        if (!response.ok) {
          const errorBody = await response.text().catch(() => '')
          throw new Error(errorBody || `Place details request failed (${response.status})`)
        }
        return response.json()
      })
      .then(placeResult => {
        const nextAddress = placeResult?.formattedAddress || prediction.description || ''
        skipNextAutocompleteFetchRef.current = true
        setDelivery(prev => ({ ...prev, address: nextAddress }))
        setAddressLookupUnavailable(false)
        clearAddressErrors()
        setAddressDropdownOpen(false)
        setAddressPredictions([])
        setAddressNoResults(false)
        setAddressActiveIndex(-1)
        placesSessionTokenRef.current = createPlacesSessionToken()
      })
      .catch(() => {
        setAddressLookupUnavailable(true)
        skipNextAutocompleteFetchRef.current = true
        setDelivery(prev => ({ ...prev, address: prediction.description || '' }))
        clearAddressErrors()
        setAddressDropdownOpen(false)
      })
  }

  const handleDeliveryAddressKeyDown = event => {
    if (!addressDropdownOpen || !addressPredictions.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setAddressActiveIndex(prev => (prev + 1) % addressPredictions.length)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setAddressActiveIndex(prev => (prev <= 0 ? addressPredictions.length - 1 : prev - 1))
      return
    }
    if (event.key === 'Enter' && addressActiveIndex >= 0) {
      event.preventDefault()
      selectDeliveryPrediction(addressPredictions[addressActiveIndex])
      return
    }
    if (event.key === 'Escape') {
      setAddressDropdownOpen(false)
      setAddressActiveIndex(-1)
    }
  }

  useEffect(() => {
    const currentAlert = errors.bounds || submitError || uploadError || ''
    const hadAlert = Boolean(previousAlertRef.current)
    const hasAlert = Boolean(currentAlert)

    // Scroll when a new alert appears or when alert text changes.
    if (hasAlert && (!hadAlert || previousAlertRef.current !== currentAlert)) {
      requestAnimationFrame(() => {
        const firstAlert = document.querySelector('.quote-form .alert')
        if (firstAlert) {
          firstAlert.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    }

    previousAlertRef.current = currentAlert
  }, [errors.bounds, submitError, uploadError])

  const validateDimensionField = (name, rawValue) => {
    const value = toNumber(rawValue)
    if (rawValue === '') return 'This field is required'
    if (name === 'width') {
      if (value < 300 || value > 1800) return 'Width must be between 300 and 1800mm'
    }
    if (name === 'depth') {
      if (value < 300 || value > 1800) return 'Depth must be between 300 and 1800mm'
    }
    if (name === 'height') {
      if (value < 18 || value > 150) return 'Height must be between 18 and 150mm'
    }
    return ''
  }

  const handleSpecInput = (field, value) => {
    const sanitized = value.replace(/[^\d]/g, '')
    setSpecs(prev => ({ ...prev, [field]: sanitized }))
    const message = validateDimensionField(field, sanitized)
    setErrors(prev => {
      const next = { ...prev }
      if (message) next[field] = message
      else delete next[field]
      const width = field === 'width' ? toNumber(sanitized) : numericSpecs.width
      const depth = field === 'depth' ? toNumber(sanitized) : numericSpecs.depth
      if (width > 1200 && depth > 1200) {
        next.bounds = 'This size combination is unavailable. Width and depth cannot both exceed 1200mm.'
      } else {
        delete next.bounds
      }
      return next
    })
  }

  const validateStep1 = () => {
    const nextErrors = {}
    const widthError = validateDimensionField('width', specs.width)
    const depthError = validateDimensionField('depth', specs.depth)
    const heightError = validateDimensionField('height', specs.height)

    if (widthError) nextErrors.width = widthError
    if (depthError) nextErrors.depth = depthError
    if (heightError) nextErrors.height = heightError

    if (numericSpecs.width > 1200 && numericSpecs.depth > 1200) {
      nextErrors.bounds = 'This size combination is unavailable. Width and depth cannot both exceed 1200mm.'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const validateStep3 = () => {
    const nextErrors = {}
    if (!contact.name) nextErrors.name = 'This field is required'
    if (!contact.email) nextErrors.email = 'This field is required'
    if (contact.email && !/\S+@\S+\.\S+/.test(contact.email)) nextErrors.email = 'Enter a valid email address'
    if (!contact.phone) nextErrors.phone = 'This field is required'

    if (uploadedFiles.length > 5) nextErrors.files = 'Maximum 5 files allowed'

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
    return Object.keys(nextErrors).length === 0
  }

  const validateStep4 = () => {
    const nextErrors = {}
    if (delivery.mode === 'Delivery') {
      const hasAddress = Boolean((delivery.address || '').trim())
      const hasAuthorityToLeave = Boolean((delivery.authorityToLeave || '').trim())

      if (!hasAddress) nextErrors.address = 'This field is required'
      if (!hasAuthorityToLeave) nextErrors.authorityToLeave = 'This field is required'
      if (!hasAddress || !hasAuthorityToLeave) {
        nextErrors.deliveryValidation = 'Please provide delivery address and authority to leave.'
      }
    }
    if (honeypot) nextErrors.honeypot = 'Spam check failed'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleFileChange = async event => {
    const selected = Array.from(event.target.files || [])
    if (!selected.length) return

    setUploadError('')
    setUploading(true)
    setErrors(prev => {
      const next = { ...prev }
      delete next.files
      return next
    })

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const valid = []
    let invalidTypeCount = 0
    let invalidSizeCount = 0
    selected.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        invalidTypeCount += 1
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        invalidSizeCount += 1
        return
      }
      valid.push(file)
    })

    const remainingSlots = Math.max(5 - uploadedFiles.length, 0)
    const uploadQueue = valid.slice(0, remainingSlots)
    const droppedCount = Math.max(valid.length - uploadQueue.length, 0)

    if (invalidTypeCount || invalidSizeCount || droppedCount) {
      const messages = []
      if (invalidTypeCount) messages.push(`${invalidTypeCount} file(s) rejected: only JPG, PNG, and PDF are supported.`)
      if (invalidSizeCount) messages.push(`${invalidSizeCount} file(s) rejected: maximum size is 10MB.`)
      if (droppedCount) messages.push(`You can upload a maximum of 5 files.`)
      setUploadError(messages.join(' '))
    }

    if (!uploadQueue.length) {
      setUploading(false)
      event.target.value = ''
      return
    }

    try {
      const uploads = await Promise.all(
        uploadQueue.map(async file => {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-')
          const path = `quote-uploads/${Date.now()}-${Math.random().toString(36).slice(2)}-${safeName}`
          const fileRef = ref(storage, path)
          const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type })
          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              snapshot => {
                const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                setUploadProgress(prev => ({ ...prev, [file.name]: percent }))
              },
              reject,
              resolve
            )
          })
          const url = await getDownloadURL(fileRef)
          setUploadProgress(prev => {
            const next = { ...prev }
            delete next[file.name]
            return next
          })
          return { name: file.name, url }
        })
      )
      setUploadedFiles(prev => [...prev, ...uploads].slice(0, 5))
    } catch (error) {
      console.log('error', error)
      setUploadError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
      event.target.value = ''
    }
  }

  const removeFile = index => {
    setUploadedFiles(prev => prev.filter((_, idx) => idx !== index))
  }

  const goNext = () => {
    if (step === 1 && !validateStep1()) return
    if (step === 3 && !validateStep3()) return
    setErrors({})
    setStep(prev => Math.min(prev + 1, 4))
  }

  const goBack = () => setStep(prev => Math.max(prev - 1, 1))

  const submitQuote = () => {
    if (!validateStep4()) {
      setSubmitError('Please complete delivery details before submitting.')
      return
    }
    setIsSubmitting(true)
    setSubmitError('')
    const payload = {
      width: numericSpecs.width,
      depth: numericSpecs.depth,
      height: numericSpecs.height,
      screenCutoff: specs.screenCutoff,
      weightKg,
      basePrice,
      screenCutoffFee,
      deliveryFee,
      gstRate: QUOTE_SETTINGS.gstRate,
      total,
      estimatedTotal: currency(basePrice + screenCutoffFee),
      name: contact.name,
      company: contact.company,
      email: contact.email,
      phone: contact.phone,
      reference: contact.reference,
      deliveryMode: delivery.mode === 'Pickup' ? 'Pick Up' : 'Delivery',
      address: delivery.address,
      deliveryNotes: delivery.notes,
      authorityToLeave: delivery.authorityToLeave,
      fileUrls: uploadedFiles.map(file => file.url),
      fileUploads: uploadedFiles,
      additionalInfo,
    }

    const submitUrl = 'https://australia-southeast1-equipsy-au.cloudfunctions.net/submitQuote'
    // const submitUrl = 'http://127.0.0.1:5001/equipsy-au/australia-southeast1/submitQuote'

    fetch(submitUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async response => {
        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}))
          throw new Error(errorBody.error || 'Submission failed.')
        }
        return response.json()
      })
      .then(response => {
        if (response?.quoteId) {
          setQuoteId(response.quoteId)
        }
        if (response?.pdfUrl) {
          setPdfUrl(response.pdfUrl)
        }
        setSubmitted(true)
        window.setTimeout(() => {
          setShowFeedback(true)
        }, 3000)
      })
      .catch(error => {
        setSubmitError(error.message || 'Submission failed.')
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  if (submitted) {
    const deliveryLabel = delivery.mode === 'Delivery' ? 'Delivery' : 'Pickup'
    const deliveryValue = delivery.mode === 'Delivery' ? currency(QUOTE_SETTINGS.deliveryFee) : 'FREE'
    const disclaimerText =
      'This quote is an estimate valid for 30 days from the date of issue. Equipsy reserves the right to revise pricing upon review of final technical drawings or if specific site requirements change (e.g., non-standard requirements or complex cut-outs). Manufacturing lead times are subject to current production capacity.'
    return (
      <div>
        <div className="quote-top-steps">
          <StepTabs step={5} />
          <StepIndicator step={5} total={5} />
        </div>
        <div className="quote-shell quote-success-page">
          <div style={{ maxWidth: '1200px', margin: 'auto' }}>
            {feedbackSubmitted && (
              <div className="feedback-success-pill" role="status" aria-live="polite">
                <span className="feedback-success-pill-badge">Success</span>
                <span>Thank you for your review!</span>
              </div>
            )}
            <div className="quote-success-card">
              <div className="quote-success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#DCFAE6" />
                  <path d="M12.1875 25.6875L20.0625 33.5625L35.8125 16.6875" stroke="#17B26A" stroke-width="3.75" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <div>
                <h2>Quote Generated Successfully!</h2>
                <p>Your quote has been sent to {contact.email}.</p>
                <div className="quote-success-id">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2.26953V6.40007C14 6.96012 14 7.24015 14.109 7.45406C14.2049 7.64222 14.3578 7.7952 14.546 7.89108C14.7599 8.00007 15.0399 8.00007 15.6 8.00007H19.7305M14 17H8M16 13H8M20 9.98822V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H12.0118C12.7455 2 13.1124 2 13.4577 2.08289C13.7638 2.15638 14.0564 2.27759 14.3249 2.44208C14.6276 2.6276 14.887 2.88703 15.4059 3.40589L18.5941 6.59411C19.113 7.11297 19.3724 7.3724 19.5579 7.67515C19.7224 7.94356 19.8436 8.2362 19.9171 8.5423C20 8.88757 20 9.25445 20 9.98822Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>

                  <span>Quote:</span>
                  <strong>{quoteId}</strong>
                </div>
                <div>
                  <small>Please reference this number in all communications</small>
                </div>
              </div>
            </div>

            <div className="final-summary">
              <div className="final-summary-header">Final Quote Summary</div>
              <div className="final-summary-body">
                <div className="final-summary-grid">
                  <div className="final-summary-image">
                    <img src={tileImage} alt="Shower base tile preview" />
                  </div>
                  <div className="final-summary-content">
                    <div className="final-summary-title">
                      <h4>Custom Shower Base Insert</h4>
                    </div>
                    <div className="final-summary-row">
                      <span>Dimensions</span>
                      <strong>{specs.width}mm (W) × {specs.depth}mm (D) × {specs.height}mm (H)</strong>
                    </div>
                    <div className="final-summary-metrics">
                      <div>
                        <span>Construction</span>
                        <strong>{getPieceLabel(specs.width, specs.depth)}</strong>
                      </div>
                      <div>
                        <span>Approx. Weight</span>
                        <strong>{weightKg.toFixed(1)} kg</strong>
                      </div>
                      <div>
                        <span>Lead Time</span>
                        <strong>{QUOTE_SETTINGS.leadTimes.manufacturing}</strong>
                      </div>
                    </div>
                    <div className="final-summary-row">
                      <span>Shower screen cut-off required?</span>
                      <strong>{specs.screenCutoff ? 'Yes' : 'No'}</strong>
                    </div>
                    <div className="final-summary-divider" />
                    <div className="final-summary-pricing">
                      <div>
                        <span>Base Fabrication</span>
                        <strong>{currency(basePrice + screenCutoffFee)}</strong>
                      </div>
                      <div>
                        <span>{deliveryLabel}</span>
                        <strong className={delivery.mode === 'Delivery' ? '' : 'free'}>{deliveryValue}</strong>
                      </div>
                      <div className="final-summary-divider" />
                      <div className="final-summary-total">
                        <span>Total (AUD)</span>
                        <strong>{currency(total)}</strong>
                      </div>
                    </div>
                    <div className="final-summary-contact">
                      <span>Contact</span>
                      <div>{contact.name || '-'}</div>
                      <div>{contact.email || '-'}</div>
                      <div>{contact.phone || '-'}</div>
                      {delivery.mode === 'Delivery' && (
                        <>
                          <span className="final-summary-contact-section-gap">Delivery Address</span>
                          <div>{delivery.address || '-'}</div>
                          <span className="final-summary-contact-section-gap">Authority to leave?</span>
                          <div>{delivery.authorityToLeave || '-'}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`quote-disclaimer${showDisclaimer ? ' open' : ''}`}
              onClick={() => setShowDisclaimer(prev => !prev)}
            >
              <div className="quote-disclaimer-header">
                <span className="quote-disclaimer-title">
                  <span aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.0001 7.49824V10.8316M10.0001 14.1649H10.0085M8.84622 3.24134L1.99215 15.0802C1.61198 15.7369 1.42189 16.0652 1.44999 16.3347C1.47449 16.5697 1.59763 16.7833 1.78876 16.9222C2.0079 17.0816 2.38728 17.0816 3.14605 17.0816H16.8542C17.613 17.0816 17.9923 17.0816 18.2115 16.9222C18.4026 16.7833 18.5258 16.5697 18.5503 16.3347C18.5783 16.0652 18.3883 15.7369 18.0081 15.0802L11.154 3.24134C10.7752 2.58704 10.5858 2.25989 10.3387 2.15001C10.1232 2.05416 9.87709 2.05416 9.66154 2.15001C9.41443 2.25989 9.22503 2.58704 8.84622 3.24134Z" stroke="#F79009" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span>Important Disclaimer</span>
                  <span className="chevron">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.57806 6.91205C3.9035 6.58661 4.43114 6.58661 4.75657 6.91205L10.0007 12.1561L15.2447 6.91205C15.5702 6.58661 16.0978 6.58661 16.4232 6.91205C16.7487 7.23748 16.7487 7.76512 16.4232 8.09056L10.5899 13.9239C10.2645 14.2493 9.73683 14.2493 9.4114 13.9239L3.57806 8.09056C3.25263 7.76512 3.25263 7.23748 3.57806 6.91205Z" fill="#F79009" />
                    </svg>
                  </span>
                </span>
              </div>
              <div className="quote-disclaimer-content">
                {disclaimerText}
              </div>
            </button>

            <div className="quote-next-steps">
              <h3>Next Steps</h3>
              <ul>
                <li><strong>Check your email</strong> - Your quote PDF has been sent to {contact.email}</li>
                <li><strong>Review the details</strong> - Make sure everything looks correct</li>
                <li><strong>Ready to proceed?</strong> Reply to the email or call us at <strong>03 9087 2199</strong> with your quote reference: <strong>{quoteId}</strong></li>
                <li><strong>We’ll send you</strong> a final Technical Dimension Form and Tax Invoice for payment</li>
                <li><strong>Production begins</strong> once payment is confirmed</li>
              </ul>
            </div>

            <div className="quote-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setSubmitted(false)
                  setStep(1)
                  setSpecs({ width: '950', depth: '800', height: '40', screenCutoff: false })
                  setContact({ name: '', company: '', email: '', phone: '', reference: '' })
                  setDelivery({ mode: 'Pickup', address: '', notes: '', authorityToLeave: '' })
                  setAdditionalInfo('')
                  setUploadedFiles([])
                  setQuoteId('')
                  setPdfUrl('')
                  setShowDisclaimer(false)
                  setFeedbackSubmitted(false)
                }}
              >
                CREATE ANOTHER QUOTE
              </button>
              <a
                className="primary"
                href={pdfUrl || '#'}
                target="_blank"
                rel="noreferrer"
                download={pdfUrl ? `Equipsy-Quote-${quoteId || 'quote'}.pdf` : undefined}
                aria-disabled={!pdfUrl}
              >
                DOWNLOAD MY QUOTE
                <span className="download-icon" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15.575C11.8667 15.575 11.7417 15.5543 11.625 15.513C11.5083 15.4717 11.4 15.4007 11.3 15.3L7.7 11.7C7.5 11.5 7.404 11.2667 7.412 11C7.42 10.7333 7.516 10.5 7.7 10.3C7.9 10.1 8.13767 9.996 8.413 9.988C8.68833 9.98 8.92567 10.0757 9.125 10.275L11 12.15V5C11 4.71667 11.096 4.47934 11.288 4.288C11.48 4.09667 11.7173 4.00067 12 4C12.2827 3.99934 12.5203 4.09534 12.713 4.288C12.9057 4.48067 13.0013 4.718 13 5V12.15L14.875 10.275C15.075 10.075 15.3127 9.979 15.588 9.987C15.8633 9.995 16.1007 10.0993 16.3 10.3C16.4833 10.5 16.5793 10.7333 16.588 11C16.5967 11.2667 16.5007 11.5 16.3 11.7L12.7 15.3C12.6 15.4 12.4917 15.471 12.375 15.513C12.2583 15.555 12.1333 15.5757 12 15.575ZM6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.19667 19.0217 4.00067 18.5507 4 18V16C4 15.7167 4.096 15.4793 4.288 15.288C4.48 15.0967 4.71733 15.0007 5 15C5.28267 14.9993 5.52033 15.0953 5.713 15.288C5.90567 15.4807 6.00133 15.718 6 16V18H18V16C18 15.7167 18.096 15.4793 18.288 15.288C18.48 15.0967 18.7173 15.0007 19 15C19.2827 14.9993 19.5203 15.0953 19.713 15.288C19.9057 15.4807 20.0013 15.718 20 16V18C20 18.55 19.8043 19.021 19.413 19.413C19.0217 19.805 18.5507 20.0007 18 20H6Z" fill="white" />
                  </svg>
                </span>
              </a>
            </div>

            <div className="quote-help-card">
              <div>
                <div className='quote-help-card-top'>
                  <div>
                    <h3>Questions about your quote?</h3>
                    <p>Contact our friendly team to request a quote, product sample, or for any other details.</p>
                  </div>

                  <a className="primary" href="https://equipsy.com.au/contact-us/" target="_blank" rel="noreferrer" style={{ marginBottom: '10px' }}>
                    Contact Us
                  </a>

                </div>

                <hr style={{ color: '#FFFFFF', marginTop: '25px', marginBottom: '10px' }} />
                <div className="quote-help-meta">
                  <span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.5569 14.906L15.1019 15.359C15.1019 15.359 14.0189 16.435 11.0639 13.497C8.10886 10.559 9.19186 9.48298 9.19186 9.48298L9.47786 9.19698C10.1849 8.49498 10.2519 7.36698 9.63486 6.54298L8.37486 4.85998C7.61086 3.83998 6.13586 3.70498 5.26086 4.57498L3.69086 6.13498C3.25786 6.56698 2.96786 7.12498 3.00286 7.74498C3.09286 9.33198 3.81086 12.745 7.81486 16.727C12.0619 20.949 16.0469 21.117 17.6759 20.965C18.1919 20.917 18.6399 20.655 19.0009 20.295L20.4209 18.883C21.3809 17.93 21.1109 16.295 19.8829 15.628L17.9729 14.589C17.1669 14.152 16.1869 14.28 15.5569 14.906Z" fill="white" />
                    </svg>

                    (03) 9087 2199
                  </span>
                  <span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M22.54 4.92596C22.5877 5.05637 22.6148 5.19348 22.6201 5.33221V18.6662C22.6201 19.0198 22.4795 19.3586 22.2295 19.6086C21.9795 19.8586 21.6407 19.9992 21.2871 19.9992H2.62012C2.26661 19.9991 1.92771 19.8586 1.67773 19.6086C1.42777 19.3586 1.28716 19.0197 1.28711 18.6662V5.33221C1.28856 5.21974 1.30445 5.10775 1.33398 4.99921L10.9541 14.593C11.2038 14.8411 11.5415 14.9806 11.8936 14.9807C12.2458 14.9807 12.5842 14.8413 12.834 14.593L22.54 4.92596ZM2.60742 17.7131V18.6662H3.53418L8.39355 13.8459L7.4541 12.9064L2.60742 17.7131ZM15.4805 13.8459L20.3408 18.6662H21.2734L21.2676 17.7131L16.4209 12.9064L15.4805 13.8459ZM21.5537 3.99921L11.8936 13.6457L2.26074 4.05292C2.37552 4.01941 2.4947 4.00105 2.61426 3.99921H21.2803C21.3711 3.98985 21.4629 3.98985 21.5537 3.99921Z" fill="white" />
                    </svg>
                    sales@equipsy.com.au
                  </span>
                  <span>
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.0007 0.0100806C10.7241 -0.0497923 9.44912 0.157538 8.25728 0.6188C7.06545 1.08006 5.98305 1.78509 5.07939 2.68876C4.17572 3.59243 3.47069 4.67482 3.00943 5.86666C2.54816 7.05849 2.34083 8.33351 2.40071 9.61008C2.40071 12.6341 4.80071 15.6101 6.00071 16.8101C7.20071 18.0101 12.0007 24.0101 12.0007 24.0101C12.0007 24.0101 16.8007 18.0101 18.0007 16.8101C19.2007 15.6101 21.6007 12.6341 21.6007 9.61008C21.6606 8.33351 21.4532 7.05849 20.992 5.86666C20.5307 4.67482 19.8257 3.59243 18.922 2.68876C18.0184 1.78509 16.936 1.08006 15.7441 0.6188C14.5523 0.157538 13.2773 -0.0497923 12.0007 0.0100806ZM12.0007 13.5101C11.2294 13.5101 10.4753 13.2814 9.83398 12.8528C9.19263 12.4243 8.69276 11.8152 8.39758 11.1025C8.10239 10.3899 8.02516 9.60575 8.17564 8.84923C8.32613 8.0927 8.69756 7.39779 9.24299 6.85236C9.78841 6.30694 10.4833 5.9355 11.2399 5.78502C11.9964 5.63454 12.7805 5.71177 13.4932 6.00695C14.2058 6.30213 14.8149 6.80201 15.2434 7.44336C15.672 8.08471 15.9007 8.83873 15.9007 9.61008C15.9007 10.1222 15.7998 10.6294 15.6038 11.1025C15.4078 11.5757 15.1206 12.0056 14.7584 12.3678C14.3963 12.7299 13.9663 13.0172 13.4932 13.2132C13.02 13.4092 12.5129 13.5101 12.0007 13.5101Z" fill="white" />
                    </svg>
                    49a Quinn St, Preston VIC 3072
                  </span>
                </div>
              </div>

            </div>
          </div>
          {showFeedback && (
            <FeedbackModal
              onClose={() => setShowFeedback(false)}
              onSubmitted={() => setFeedbackSubmitted(true)}
              feedbackContext={{
                quoteId,
                contactEmail: contact.email,
                contactName: contact.name,
              }}
            />
          )}
        </div>
      </div>
    )
  }

  const layoutClass = step === 2 || step === 3 || step === 4 ? 'quote-layout single' : 'quote-layout'

  return (
    <div>
      <header className="quote-top-steps">
        <StepTabs step={step} />
        <StepIndicator step={step} total={5} />
      </header>
      <div className="quote-shell">
        <div className={layoutClass}>
          <div className="quote-form">
            {errors.bounds && <div className="alert">{errors.bounds}</div>}

            {step === 1 && (
              <>
                {step > 1 && (
                  <button type="button" className="text-link" onClick={goBack}>
                    Back
                  </button>
                )}
                <StepHeader
                  title="Let’s get started."
                  bold
                  subtitle="Fill in your shower base details."
                />
                <p className="quote-intro">Enter your dimensions to get an instant quote in 60 seconds.</p>
                <div className="quote-preview-inline-mobile">
                  <ShowerImageCard />
                  <HelpCard onHelp={() => setShowHelp(true)} />
                </div>
                <InputField label={<span>Width <TooltipIcon text="Measure left to right, internal edge to edge." /></span>} error={errors.width} hint="Min 300 mm · Max 1800 mm">
                  <div className="input-inline">
                    <input
                      type="text"
                      value={specs.width}
                      onChange={event => handleSpecInput('width', event.target.value)}
                      inputMode="numeric"
                    />
                    <span className="unit-suffix">mm</span>
                  </div>
                </InputField>
                <InputField label={<span>Depth <TooltipIcon text="Measure from the back wall to the front edge of the base." /></span>} error={errors.depth} hint="Min 300 mm · Max 1800 mm">
                  <div className="input-inline">
                    <input
                      type="text"
                      value={specs.depth}
                      onChange={event => handleSpecInput('depth', event.target.value)}
                      inputMode="numeric"
                    />
                    <span className="unit-suffix">mm</span>
                  </div>
                </InputField>
                <InputField label={<span>Height <TooltipIcon text="Measure from the shower floor (inside the shower area) to the top of the entry step." /></span>} error={errors.height} hint="Min 18 mm · Max 150 mm">
                  <div className="input-inline">
                    <input
                      type="text"
                      value={specs.height}
                      onChange={event => handleSpecInput('height', event.target.value)}
                      inputMode="numeric"
                    />
                    <span className="unit-suffix">mm</span>
                  </div>
                </InputField>
                <InputField label={<span>Is a shower screen cut-off required? <TooltipIcon align="end" text="Select Yes if there is an inward-swinging shower door that is not being removed. The base will include a cutout under the door to allow opening and lifting for cleaning." /></span>} required>
                  <div className="pill-toggle">
                    {['Yes', 'No'].map(option => (
                      <button
                        key={option}
                        type="button"
                        className={specs.screenCutoff === (option === 'Yes') ? 'pill active' : 'pill'}
                        onClick={() => setSpecs(prev => ({ ...prev, screenCutoff: option === 'Yes' }))}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </InputField>
              </>
            )}

            {step === 2 && (
              <>
                <StepHeader
                  title="Your Custom Shower Base"
                  subtitle="Here's your estimated price based on your specifications"
                />
                <div style={{ maxWidth: '790px', margin: 'auto' }}>
                  <div className="instant-badge"><span aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.75 6L3.14223 6.78446C3.27497 7.04995 3.34135 7.18269 3.43002 7.29772C3.5087 7.3998 3.6002 7.4913 3.70228 7.56998C3.81731 7.65865 3.95005 7.72503 4.21554 7.85777L5 8.25L4.21554 8.64223C3.95005 8.77497 3.81731 8.84135 3.70228 8.93002C3.6002 9.0087 3.5087 9.1002 3.43002 9.20228C3.34135 9.31731 3.27497 9.45005 3.14223 9.71554L2.75 10.5L2.35777 9.71554C2.22503 9.45005 2.15865 9.31731 2.06998 9.20228C1.9913 9.1002 1.8998 9.0087 1.79772 8.93002C1.68269 8.84135 1.54995 8.77497 1.28446 8.64223L0.5 8.25L1.28446 7.85777C1.54995 7.72503 1.68269 7.65865 1.79772 7.56998C1.8998 7.4913 1.9913 7.3998 2.06998 7.29772C2.15865 7.18269 2.22503 7.04995 2.35777 6.78446L2.75 6Z" stroke="#0580CC" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M7 0.5L7.58931 2.03221C7.73032 2.39882 7.80082 2.58213 7.91046 2.73632C8.00763 2.87298 8.12702 2.99237 8.26368 3.08954C8.41787 3.19918 8.60118 3.26968 8.96779 3.41069L10.5 4L8.96779 4.58931C8.60118 4.73032 8.41787 4.80082 8.26368 4.91046C8.12702 5.00763 8.00763 5.12702 7.91046 5.26368C7.80082 5.41787 7.73032 5.60118 7.58931 5.96779L7 7.5L6.41069 5.96779C6.26968 5.60118 6.19918 5.41787 6.08954 5.26368C5.99237 5.12702 5.87298 5.00763 5.73632 4.91046C5.58213 4.80082 5.39882 4.73032 5.03221 4.58931L3.5 4L5.03221 3.41069C5.39882 3.26968 5.58213 3.19918 5.73632 3.08954C5.87298 2.99237 5.99237 2.87298 6.08954 2.73632C6.19918 2.58213 6.26968 2.39882 6.41069 2.03221L7 0.5Z" stroke="#0580CC" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>Instant Quote</div>
                  <div className="estimate-card-large">
                    <div className="estimate-center">
                      <span>Estimated Total</span>
                      <strong>{currency(basePrice + screenCutoffFee)}</strong>
                      <small>Inc. GST</small>
                    </div>
                    <div className="estimate-row">
                      <span>Base Fabrication</span>
                      <strong>{currency(basePrice + screenCutoffFee)}</strong>
                    </div>
                  </div>

                  <div className="estimate-grid compact">
                    <div className="mini-card">
                      <div className="mini-icon">
                        <img src={constructionImage} alt="" aria-hidden="true" />
                      </div>
                      <div>
                        <span>Construction <TooltipIcon align="end" text="Bases wider than 1200mm are manufactured in two pieces to make lifting easier, particularly for elderly users." /></span>
                        <strong>{getPieceLabel(specs.width, specs.depth)}</strong>
                        <small>{toNumber(specs.width) > 1200 || toNumber(specs.depth) > 1200 ? 'Two-piece construction' : 'Single-piece construction'}</small>
                      </div>
                    </div>
                    <div className="mini-card">
                      <div className="mini-icon">
                        <img src={approxWeightImage} alt="" aria-hidden="true" />
                      </div>
                      <div>
                        <span>Approx. Weight <TooltipIcon align="end" text="This is the estimated weight of the shower base only and does not include packaging." /></span>
                        <strong>{weightKg.toFixed(1)} kg</strong>
                        <small>For handling planning</small>
                      </div>
                    </div>
                    <div className="mini-card">
                      <div className="mini-icon">
                        <img src={manufacturingImage} alt="" aria-hidden="true" />
                      </div>
                      <div>
                        <span>Manufacturing <TooltipIcon align="end" text="All shower base inserts are custom made to order. Standard manufacturing lead times apply. Shipping takes an additional 2–5 business days depending on your location." /></span>
                        <strong>{QUOTE_SETTINGS.leadTimes.manufacturing}</strong>
                        <small>excluding delivery</small>
                      </div>
                    </div>
                  </div>

                  <div className="specs-summary">
                    <h3>Your Specifications</h3>
                    <div className="specs-grid">
                      <div>
                        <span style={{ marginBottom: 0 }}>Dimensions</span>
                        <p style={{ margin: 0 }}>{specs.width}mm (W) × {specs.depth}mm (D) × {specs.height}mm (H)</p>
                      </div>
                      <div>
                        <span>Screen Cut-out</span>
                        <div className="specs-pill">
                          {specs.screenCutoff ? 'Required' : 'Not Required'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="quote-ready">
                    <h3>Ready to Get Your Formal Quote?</h3>
                    <p>We will generate a formal PDF quote including shipping and email it to you instantly.</p>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <StepHeader
                  title="Contact Details"
                  subtitle="We'll send your PDF quote to this email"
                />
                <div className="contact-section">
                  <div className="info-grid">
                    <div className="info-card required-card">
                      <div className="info-card-header">
                        <span className="pill-tag required">Required</span>
                        <h4>Essential Information</h4>
                      </div>
                      <InputField label="Contact Name" required error={errors.name}>
                        <input
                          value={contact.name}
                          onChange={event => setContact(prev => ({ ...prev, name: event.target.value }))}
                          placeholder="John Smith"
                        />
                      </InputField>
                      <InputField label="Email address" required error={errors.email} hint="Your PDF quote will be sent here">
                        <input
                          type="email"
                          value={contact.email}
                          onChange={event => setContact(prev => ({ ...prev, email: event.target.value }))}
                          placeholder="youremail@example.com"
                        />
                      </InputField>
                      <InputField label="Phone number" required error={errors.phone} hint="In case we need to clarify details">
                        <input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={contact.phone}
                          onChange={event => {
                            const digitsOnly = event.target.value.replace(/\D/g, '')
                            setContact(prev => ({ ...prev, phone: digitsOnly }))
                          }}
                        />
                      </InputField>
                    </div>

                    <div className="info-card">
                      <div className="info-card-header">
                        <span className="pill-tag optional">Optional</span>
                        <h4>Optional Information</h4>
                      </div>
                      <InputField label="Company">
                        <input
                          value={contact.company}
                          onChange={event => setContact(prev => ({ ...prev, company: event.target.value }))}
                        />
                      </InputField>
                      <InputField label="Order Reference" hint="">
                        <input
                          value={contact.reference}
                          onChange={event => setContact(prev => ({ ...prev, reference: event.target.value }))}
                        />
                      </InputField>
                    </div>
                  </div>
                </div>
                <div className="section-label">
                  <span className="pill-tag optional">Optional</span>
                  <h4>Upload Photos</h4>
                </div>
                <p className="section-hint">Upload photos of your current shower base or bathroom space to help us better understand your requirements.</p>
                <InputField label="" error={errors.files} hint="">
                  <div
                    className={`upload-drop${dragActive ? ' drag-active' : ''}`}
                    onDragEnter={() => setDragActive(true)}
                    onDragOver={event => {
                      event.preventDefault()
                      setDragActive(true)
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={() => setDragActive(false)}
                  >
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                      disabled={uploading || uploadedFiles.length >= 5}
                    />
                    <div className="upload-icon"> <img src={Upload} style={{ width: '48px', height: '48px' }} /></div>
                    <p>Choose files or drag and drop them here</p>
                    <small style={{ color: "#9BD7FD", fontWeight: 'bold' }}>JPEG, PNG, and PDF formats, up to 10MB</small>
                  </div>
                </InputField>
                {!!Object.keys(uploadProgress).length && (
                  <div className="upload-progress-list">
                    {Object.entries(uploadProgress).map(([name, progress]) => (
                      <div key={name} className="upload-progress-item">
                        <span>{name}</span>
                        <span>{progress}%</span>
                      </div>
                    ))}
                  </div>
                )}
                {uploadError && <div className="alert">{uploadError}</div>}
                {uploadedFiles.length > 0 && (
                  <div className="upload-thumbs">
                    {uploadedFiles.map((file, index) => {
                      const isImage = /\.(png|jpe?g)$/i.test(file.name)
                      return (
                        <div key={`${file.name}-${index}`} className="upload-thumb">
                          {isImage ? (
                            <img src={file.url} alt={file.name} />
                          ) : (
                            <div className="upload-doc">PDF</div>
                          )}
                          <button type="button" onClick={() => removeFile(index)} aria-label="Remove file">
                            ×
                          </button>
                          <span className="upload-name" title={file.name}>{file.name}</span>
                        </div>
                      )
                    })}
                  </div>
                )}

                <div className="section-label">
                  <span className="pill-tag optional">Optional</span>
                  <h4>Additional Information</h4>
                </div>
                <p className="section-hint">Please share any additional details, special requirements, or questions you may have.</p>
                <InputField label="">
                  <textarea
                    rows="4"
                    placeholder="E.g., installation timeline, specific measurements, access restrictions, or any special requirements..."
                    value={additionalInfo}
                    onChange={event => setAdditionalInfo(event.target.value)}
                  />
                </InputField>
              </>
            )}

            {step === 4 && (
              <>
                <StepHeader
                  title="Delivery or Pickup?"
                  subtitle="Delivery costs will be added as part of the quote."
                />
                <div className="delivery-grid">
                  <button
                    type="button"
                    className={delivery.mode === 'Pickup' ? 'delivery-card active' : 'delivery-card'}
                    onClick={() => setDelivery(prev => ({ ...prev, mode: 'Pickup' }))}
                  >
                    <div className="delivery-card-media" aria-hidden="true">
                      <img className="delivery-photo" src={pickupCoverImage} alt="" />
                    </div>
                    <div className="delivery-card-body">
                      <div className="delivery-card-head">
                        <div className="delivery-icon">
                          <img src={pickupImage} alt="" aria-hidden="true" />
                        </div>
                        <div>
                          <span className="delivery-card-title">Pickup / Click & Collect <span className="delivery-pill success">FREE</span></span>
                          <div className="delivery-card-sub">Collect from our warehouse when your order is ready</div>
                        </div>
                      </div>
                      <div className="delivery-meta">
                        <div className="delivery-meta-line with-icon">
                          <img className="delivery-meta-icon" src={mapIcon} alt="" aria-hidden="true" />
                          <span>49a Quinn St Preston VIC 3072</span>
                        </div>
                        <div className="delivery-meta-line">Mon-Fri: 8:00 AM - 4:00 PM</div>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={delivery.mode === 'Delivery' ? 'delivery-card active' : 'delivery-card'}
                    onClick={() => setDelivery(prev => ({ ...prev, mode: 'Delivery' }))}
                  >
                    <div className="delivery-card-media" aria-hidden="true">
                      <img className="delivery-photo" src={deliveryCoverImage} alt="" />
                    </div>
                    <div className="delivery-card-body">
                      <div className="delivery-card-head">
                        <div className="delivery-icon">
                          <img src={deliveryImage} alt="" aria-hidden="true" />
                        </div>
                        <div>
                          <span className="delivery-card-title">Delivery to Site <span className="delivery-pill price">{currency(QUOTE_SETTINGS.deliveryFee)}</span></span>
                          <div className="delivery-card-sub">We'll deliver directly to your address</div>
                        </div>
                      </div>
                      <div className="delivery-meta">
                        <div className="delivery-meta-line">Flat rate delivery fee</div>
                        <div className="delivery-meta-line">Estimated 2-5 business days (varies by location)</div>
                      </div>
                    </div>
                  </button>
                </div>
                {delivery.mode === 'Delivery' && (
                  <>
                    <h4 className="delivery-details-heading">Delivery Details</h4>
                    <InputField
                      label="Delivery Address"
                      required
                      error={errors.address}
                      hint={addressLookupUnavailable
                        ? 'Enter your full street address. Note: We do not deliver to PO boxes.'
                        : 'Search and select your address from Google suggestions. We do not deliver to PO boxes.'}
                    >
                      <div ref={deliveryAutocompleteContainerRef} className="delivery-autocomplete-widget">
                        <input
                          value={delivery.address}
                          onChange={event => {
                            skipNextAutocompleteFetchRef.current = false
                            setDelivery(prev => ({ ...prev, address: event.target.value }))
                            clearAddressErrors()
                          }}
                          onFocus={() => {
                            if (delivery.address.trim().length >= 2) setAddressDropdownOpen(true)
                          }}
                          onKeyDown={handleDeliveryAddressKeyDown}
                          placeholder="Start typing your address..."
                          autoComplete="street-address"
                          role="combobox"
                          aria-autocomplete="list"
                          aria-expanded={addressDropdownOpen}
                          aria-controls="delivery-address-suggestions"
                        />
                        {!addressLookupUnavailable && addressDropdownOpen && (
                          <div className="delivery-autocomplete-dropdown" role="listbox" id="delivery-address-suggestions">
                            {addressLoading && (
                              <div className="delivery-autocomplete-status">Searching addresses...</div>
                            )}
                            {!addressLoading && addressPredictions.map((prediction, index) => (
                              <button
                                key={prediction.place_id}
                                type="button"
                                role="option"
                                aria-selected={index === addressActiveIndex}
                                className={`delivery-autocomplete-item${index === addressActiveIndex ? ' active' : ''}`}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectDeliveryPrediction(prediction)}
                              >
                                {prediction.description}
                              </button>
                            ))}
                            {!addressLoading && addressNoResults && (
                              <div className="delivery-autocomplete-status">No matching addresses found.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </InputField>
                    <InputField label="Authority to Leave" required error={errors.authorityToLeave} hint="Can we leave the delivery if you're not home?">
                      <div className="entry-grid">
                        {['Yes', 'No'].map(option => (
                          <button
                            key={option}
                            type="button"
                            className={delivery.authorityToLeave === option ? 'chip active' : 'chip'}
                            onClick={() => setDelivery(prev => ({ ...prev, authorityToLeave: option }))}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </InputField>
                    <InputField label="Delivery Notes">
                      <textarea
                        rows="3"
                        placeholder="e.g. Call on arrival, Gate code:1234, Deliver to rear of property"
                        value={delivery.notes}
                        onChange={event => setDelivery(prev => ({ ...prev, notes: event.target.value }))}
                      />
                    </InputField>
                  </>
                )}

                <div className="section-label">
                  <h4>Send the request</h4>
                </div>
                <div className="final-summary">
                  <div className="final-summary-header">Final Quote Summary</div>
                  <div className="final-summary-body">
                    <div className="final-summary-grid">
                      <div className="final-summary-image">
                        <img src={tileImage} alt="Shower base tile preview" />
                      </div>
                      <div className="final-summary-content">
                        <div className="final-summary-title">
                          <h4>Custom Shower Base Insert</h4>
                          <button type="button" className="text-link" onClick={() => setStep(1)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.5262 3.94531H4.89472C4.39221 3.94531 3.91028 4.14493 3.55495 4.50026C3.19962 4.85559 3 5.33752 3 5.84003V19.103C3 19.6056 3.19962 20.0875 3.55495 20.4428C3.91028 20.7981 4.39221 20.9978 4.89472 20.9978H18.1577C18.6602 20.9978 19.1422 20.7981 19.4975 20.4428C19.8528 20.0875 20.0524 19.6056 20.0524 19.103V12.4715" stroke="#4D5761" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M17.5651 3.58861C17.9419 3.21173 18.4531 3 18.9861 3C19.5191 3 20.0302 3.21173 20.4071 3.58861C20.784 3.9655 20.9957 4.47666 20.9957 5.00965C20.9957 5.54264 20.784 6.05381 20.4071 6.43069L11.8686 14.9702C11.6436 15.1949 11.3657 15.3595 11.0605 15.4486L8.33873 16.2444C8.25721 16.2681 8.1708 16.2696 8.08854 16.2485C8.00628 16.2274 7.9312 16.1846 7.87116 16.1246C7.81112 16.0645 7.76832 15.9895 7.74724 15.9072C7.72617 15.8249 7.72759 15.7385 7.75137 15.657L8.54715 12.9352C8.6367 12.6302 8.80155 12.3527 9.02651 12.1281L17.5651 3.58861Z" stroke="#4D5761" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            Edit order
                          </button>
                        </div>
                        <div className="final-summary-row">
                          <span>Dimensions</span>
                          <strong style={{ fontWeight: '600' }}>{specs.width}mm (W) × {specs.depth}mm (D) × {specs.height}mm (H)</strong>
                        </div>
                        <div className="final-summary-metrics">
                          <div>
                            <span>Construction</span>
                            <strong>{getPieceLabel(specs.width, specs.depth)}</strong>
                          </div>
                          <div>
                            <span>Approx. Weight</span>
                            <strong>{weightKg.toFixed(1)} kg</strong>
                          </div>
                          <div>
                            <span>Lead Time</span>
                            <strong>{QUOTE_SETTINGS.leadTimes.manufacturing}</strong>
                          </div>
                        </div>
                        <div className="final-summary-row">
                          <span>Shower screen cut-off required?</span>
                          <strong>{specs.screenCutoff ? 'Yes' : 'No'}</strong>
                        </div>
                        <div className="final-summary-divider" />
                        <div className="final-summary-pricing">
                          <div>
                            <span>Base Fabrication</span>
                            <strong>{currency(basePrice + screenCutoffFee)}</strong>
                          </div>
                          <div>
                            <span>{delivery.mode === 'Delivery' ? 'Delivery' : 'Pickup'}</span>
                            <strong className={delivery.mode === 'Delivery' ? '' : 'free'}>
                              {delivery.mode === 'Delivery' ? currency(QUOTE_SETTINGS.deliveryFee) : 'FREE'}
                            </strong>
                          </div>
                          <div className="final-summary-divider" />
                          <div className="final-summary-total">
                            <span>Total (AUD)</span>
                            <strong>{currency(total)}</strong>
                          </div>
                        </div>
                        <div className="final-summary-contact">
                          <span>Contact</span>
                          <div>{contact.name || '-'}</div>
                          <div>{contact.email || '-'}</div>
                          <div>{contact.phone || '-'}</div>
                          {delivery.mode === 'Delivery' && (
                            <>
                              <span className="final-summary-contact-section-gap">Delivery Address</span>
                              <div>{delivery.address || '-'}</div>
                              <span className="final-summary-contact-section-gap">Authority to leave?</span>
                              <div>{delivery.authorityToLeave || '-'}</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {submitError && <div className="alert">{submitError}</div>}
                <input
                  type="text"
                  className="honeypot"
                  value={honeypot}
                  onChange={event => setHoneypot(event.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </>
            )}

            <div className="step-controls">
              <button type="button" className="ghost prev-step-btn" onClick={goBack} disabled={step === 1} aria-label="Previous">
                <ChevronIcon direction="left" />
                <span className="prev-label">PREVIOUS</span>
              </button>
              {step < 4 ? (
                <button type="button" className="primary" onClick={goNext}>
                  {step === 1 ? (
                    <>
                      <span className="step-btn-label">SEE MY QUICK QUOTE</span>
                      <ChevronIcon />
                    </>
                  ) : (<>
                    {step === 2 ? (
                      <>
                        <span className="file-btn-icon" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.6673 1.89258V5.3347C11.6673 5.80141 11.6673 6.03476 11.7581 6.21302C11.838 6.36982 11.9655 6.49731 12.1223 6.5772C12.3006 6.66803 12.5339 6.66803 13.0007 6.66803H16.4428M13.334 10.8346H6.66732M13.334 14.168H6.66732M8.33398 7.5013H6.66732M11.6673 1.66797H7.33398C5.93385 1.66797 5.23379 1.66797 4.69901 1.94045C4.2286 2.18014 3.84615 2.56259 3.60647 3.03299C3.33398 3.56777 3.33398 4.26784 3.33398 5.66797V14.3346C3.33398 15.7348 3.33398 16.4348 3.60647 16.9696C3.84615 17.44 4.2286 17.8225 4.69901 18.0622C5.23379 18.3346 5.93385 18.3346 7.33398 18.3346H12.6673C14.0674 18.3346 14.7675 18.3346 15.3023 18.0622C15.7727 17.8225 16.1552 17.44 16.3948 16.9696C16.6673 16.4348 16.6673 15.7348 16.6673 14.3346V6.66797L11.6673 1.66797Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                        </span>
                        <span className="step-btn-label">GET A FORMAL QUOTE (PDF)</span>
                        <ChevronIcon />
                      </>
                    ) : (
                      <>
                        <span className="step-btn-label">REVIEW & SUBMIT</span>
                        <ChevronIcon />
                      </>
                    )}
                  </>
                  )}
                </button>
              ) : (
                <button type="button" className="primary" onClick={submitQuote} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="submit-loading-content">
                      <span className="submit-spinner" aria-hidden="true" />
                      SUBMITTING...
                    </span>
                  ) : (
                    <>
                      <span className="file-btn-icon" aria-hidden="true">
                        <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.16732 1.05664V4.49876C9.16732 4.96547 9.16732 5.19882 9.25815 5.37708C9.33804 5.53388 9.46552 5.66137 9.62233 5.74126C9.80058 5.83209 10.0339 5.83209 10.5007 5.83209H13.9428M10.834 9.9987H4.16732M10.834 13.332H4.16732M5.83398 6.66536H4.16732M9.16732 0.832031H4.83398C3.43385 0.832031 2.73379 0.832031 2.19901 1.10451C1.7286 1.3442 1.34615 1.72665 1.10647 2.19705C0.833984 2.73183 0.833984 3.4319 0.833984 4.83203V13.4987C0.833984 14.8988 0.833984 15.5989 1.10647 16.1337C1.34615 16.6041 1.7286 16.9865 2.19901 17.2262C2.73379 17.4987 3.43385 17.4987 4.83398 17.4987H10.1673C11.5674 17.4987 12.2675 17.4987 12.8023 17.2262C13.2727 16.9865 13.6552 16.6041 13.8948 16.1337C14.1673 15.5989 14.1673 14.8988 14.1673 13.4987V5.83203L9.16732 0.832031Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </span>
                      GENERATE MY QUOTE
                    </>
                  )}
                </button>
              )}
              <button type="button" className="help-inline-btn" onClick={() => setShowSupport(true)}>
                <span aria-hidden="true">?</span>
                HELP
              </button>
            </div>
          </div>

          {step === 1 && (
            <div className="quote-preview">
              <strong style={{ fontSize: '20px' }}>Measuring Guide</strong>
              <ShowerImageCard />
              <HelpCard onHelp={() => setShowHelp(true)} />
            </div>
          )}

          {step === 3 && null}
        </div>

        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
        {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
        <button type="button" className="help-fab-mobile" onClick={() => setShowSupport(true)} aria-label="Help">
          <span aria-hidden="true">?</span>
        </button>
        {uploading && (
          <div className="uploading-modal" role="status" aria-live="polite">
            <div className="uploading-card">
              <div className="spinner" />
              <p>Uploading files…</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GetQuote
